// Servicio de autenticación
import { apiPost, apiGet, apiPut, apiDelete, apiPostFormData, setAccessToken, setRefreshToken, clearTokens, getAccessToken } from './api';
import API_CONFIG from '../config/api';

/**
 * Verifica si el usuario actual es administrador
 * @returns {Promise<boolean>} - true si es admin, false si no
 */
export const checkIsAdmin = async () => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      console.log('checkIsAdmin: No hay token, usuario no es admin');
      return false;
    }
    
    console.log('🔍 checkIsAdmin: Verificando si usuario es admin...');
    console.log('🔍 checkIsAdmin: Token disponible:', !!token, 'Longitud:', token ? token.length : 0);
    console.log('🔍 checkIsAdmin: Endpoint:', API_CONFIG.ENDPOINTS.ADMIN.ME);
    
    const response = await apiGet(API_CONFIG.ENDPOINTS.ADMIN.ME);
    console.log('🔍 checkIsAdmin: Respuesta recibida, status:', response.status, 'ok:', response.ok);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.log('🔍 checkIsAdmin: ❌ Error en respuesta:', response.status, errorData);
      localStorage.setItem('isAdmin', 'false');
      return false;
    }
    
    const data = await response.json();
    console.log('🔍 checkIsAdmin: Datos de respuesta:', JSON.stringify(data, null, 2));
    
    if (data.success && data.data && data.data.isAdmin) {
      console.log('✅ checkIsAdmin: Usuario ES admin - Guardando en localStorage');
      localStorage.setItem('isAdmin', 'true');
      const verified = localStorage.getItem('isAdmin');
      console.log('✅ checkIsAdmin: Verificado guardado - localStorage.isAdmin =', verified);
      
      // Disparar evento inmediatamente para que Header se actualice
      window.dispatchEvent(new Event('adminStatusChanged'));
      console.log('✅ checkIsAdmin: Evento adminStatusChanged disparado');
      
      return true;
    } else {
      console.log('❌ checkIsAdmin: Usuario NO es admin - Respuesta:', data);
      localStorage.setItem('isAdmin', 'false');
      return false;
    }
  } catch (error) {
    // Si es error 403, el usuario no es admin - esto es esperado, no loguear como error
    const errorMessage = error.message || error.toString() || '';
    const is403Error = errorMessage.includes('403') || 
                       errorMessage.includes('Acceso solo para administradores') ||
                       errorMessage.includes('Forbidden');
    
    if (is403Error) {
      localStorage.setItem('isAdmin', 'false');
      return false;
    }
    
    // Solo loguear otros errores (no 403)
    console.error('checkIsAdmin: Error al verificar admin:', error);
    localStorage.setItem('isAdmin', 'false');
    return false;
  }
};

/**
 * Inicia sesión con email y contraseña
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña del usuario
 * @returns {Promise<object>} - Datos del usuario y tokens
 */
export const login = async (email, password) => {
  try {
    const response = await apiPost(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
      email,
      password,
    });
    
    const data = await response.json();
    
    // Log completo de la respuesta para debugging
    console.log('🔍 Respuesta completa del login:', JSON.stringify(data, null, 2));
    console.log('🔍 Estructura de data:', {
      hasSuccess: 'success' in data,
      success: data.success,
      hasData: 'data' in data,
      dataKeys: data.data ? Object.keys(data.data) : 'no data',
      hasAccessToken: data.data && 'access_token' in data.data,
      hasRefreshToken: data.data && 'refresh_token' in data.data,
      hasUser: data.data && 'user' in data.data
    });
    
    if (!data.success) {
      throw new Error(data.error || 'Error al iniciar sesión');
    }
    
    // Guardar tokens (soportar data.data o data en la respuesta)
    const payload = data.data || data;
    const accessToken = payload.access_token || data.access_token;
    const refreshTokenValue = payload.refresh_token || data.refresh_token;
    if (accessToken) {
      setAccessToken(accessToken);
      console.log('✅ access_token guardado');
    } else {
      console.error('❌ No se recibió access_token en la respuesta del login:', data);
    }
    if (refreshTokenValue) {
      setRefreshToken(refreshTokenValue);
    }
    
    // Guardar información del usuario
    const userPayload = payload?.user || data.data?.user || data.user;
    if (userPayload) {
      // Crear username completo si no existe (para compatibilidad)
      const userData = {
        ...userPayload,
        username: userPayload.username || `${userPayload.nombre || ''} ${userPayload.apellido || ''}`.trim() || userPayload.email
      };

      // Guardar en localStorage de forma síncrona
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('user', JSON.stringify(userData));

      // Verificar si es admin (basado en rol del backend o email específico)
      const adminEmails = ['admin@federacion.com', 'josemnaranjoc@gmail.com'];
      const userEmail = (userPayload.email || '').toLowerCase().trim();
      const isAdmin = userPayload.is_admin ||
                      userPayload.rol === 'admin' ||
                      userPayload.role === 'admin' ||
                      adminEmails.includes(userEmail);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');

      // Debug: verificar detección de admin
      console.log('🔐 Admin check:', {
        email: userEmail,
        adminEmails,
        is_admin: data.data.user.is_admin,
        rol: data.data.user.rol,
        role: data.data.user.role,
        isAdmin
      });
      
      console.log('Login exitoso, usuario guardado:', userData);
      console.log('isLoggedIn:', localStorage.getItem('isLoggedIn'));
      console.log('user:', localStorage.getItem('user'));
      
      // Verificar si el usuario es admin después del login
      // IMPORTANTE: Hacer esto después de guardar el usuario para tener el token disponible
      try {
        console.log('Login: Iniciando verificación de admin...');
        const isAdminResult = await checkIsAdmin();
        console.log('Login: Resultado de verificación admin:', isAdminResult);
        console.log('Login: isAdmin en localStorage después de verificación:', localStorage.getItem('isAdmin'));
        
        // Disparar evento para que Header se actualice
        window.dispatchEvent(new Event('adminStatusChanged'));
      } catch (error) {
        console.error('Login: Error al verificar admin:', error);
        // No es crítico si falla la verificación de admin, pero loggear el error
      }
      
      // Disparar evento personalizado para que otros componentes se actualicen
      // Usar requestAnimationFrame para asegurar que el DOM esté listo
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('userLogin'));
      });
    }
    
    return data.data;
  } catch (error) {
    // Mejorar mensaje de error para debugging
    console.error('Error en login:', error);
    if (error.message.includes('conexión')) {
      throw new Error(`${error.message} URL: ${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`);
    }
    throw error;
  }
};

/**
 * Registra un nuevo usuario
 * @param {object} userData - Datos del usuario
 * @param {string} userData.email - Email
 * @param {string} userData.password - Contraseña
 * @param {string} userData.nombre - Nombre
 * @param {string} userData.apellidoPaterno - Apellido paterno
 * @param {string} userData.apellidoMaterno - Apellido materno
 * @param {string} userData.rut - RUT (opcional)
 * @param {string} userData.telefono - Teléfono (opcional, no se envía al backend actualmente)
 * @returns {Promise<object>} - Datos del usuario creado
 */
export const register = async (userData) => {
  try {
    // Construir payload solo con campos que tienen valor
    // Esto evita enviar undefined en el JSON (que se omite)
    const payload = {
      email: userData.email,
      password: userData.password,
      nombre: userData.nombre,
      apellido: userData.apellidoPaterno,
      rut: userData.rut, // RUT sin puntos ni guión
    };
    
    // Agregar campos opcionales solo si tienen valor (no undefined, null, o string vacío)
    if (userData.telefono && userData.telefono.trim() !== '') {
      payload.telefono = userData.telefono.trim();
    }
    if (userData.fechaNacimiento && userData.fechaNacimiento.trim() !== '') {
      payload.fecha_nacimiento = userData.fechaNacimiento;
    }
    if (userData.genero && userData.genero.trim() !== '') {
      payload.genero = userData.genero; // 'M' o 'F'
    }
    if (userData.region && userData.region.trim() !== '') {
      payload.region = userData.region.trim();
    }
    if (userData.ciudad && userData.ciudad.trim() !== '') {
      payload.ciudad = userData.ciudad.trim();
    }
    if (userData.pais && userData.pais.trim() !== '') {
      payload.pais = userData.pais.trim();
    }
    
    const response = await apiPost(API_CONFIG.ENDPOINTS.AUTH.REGISTER, payload);
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Error al registrar usuario');
    }
    
    // Solo guardar tokens y marcar como logueado si el backend devuelve tokens
    // (con verificación de email, no hay tokens hasta que verifique)
    if (data.data.access_token) {
      setAccessToken(data.data.access_token);
      setRefreshToken(data.data.refresh_token || null);
      localStorage.setItem('isLoggedIn', 'true');
      const user = {
        ...data.data,
        telefono: userData.telefono,
        fecha_nacimiento: userData.fechaNacimiento,
        genero: userData.genero,
        region: userData.region,
        ciudad: userData.ciudad,
        pais: userData.pais,
        username: data.data.username || `${userData.nombre} ${userData.apellidoPaterno}`.trim() || data.data.email
      };
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('userLogin'));
    }
    
    return data.data;
  } catch (error) {
    // Mejorar mensajes de error para debugging
    console.error('Error en register:', error);
    
    // Si el error viene del backend (error.message contiene detalles), mantenerlo
    // Si es error de conexión, ya está manejado en api.js
    if (error.message && error.message.includes('UndefinedColumn')) {
      throw new Error('Error de configuración en la base de datos: la tabla de usuarios no tiene la estructura esperada. Por favor contacta al administrador.');
    }
    
    // Si el error ya tiene un mensaje descriptivo, mantenerlo
    if (error.message) {
      throw error;
    }
    
    // Error genérico como último recurso
    throw new Error('Error al registrar usuario. Por favor verifica tus datos e intenta nuevamente.');
  }
};

/**
 * Obtiene la información del usuario actual
 * @returns {Promise<object>} - Datos del usuario
 */
export const getCurrentUser = async () => {
  // Verificar explícitamente si hay token ANTES de cualquier otra operación
  const token = getAccessToken();
  
  if (!token) {
    console.error('authService.getCurrentUser: ❌ No hay token disponible. El usuario necesita hacer login nuevamente.');
    console.error('authService.getCurrentUser: Estado de localStorage:', {
      access_token: localStorage.getItem('access_token') ? 'presente' : 'ausente',
      refresh_token: localStorage.getItem('refresh_token') ? 'presente' : 'ausente',
      isLoggedIn: localStorage.getItem('isLoggedIn'),
      user: localStorage.getItem('user') ? 'presente' : 'ausente',
      allKeys: Object.keys(localStorage)
    });
    // Limpiar estado inconsistente
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    throw new Error('No hay token de autenticación. Por favor, inicia sesión nuevamente.');
  }
  
  try {
    console.log('authService.getCurrentUser: Token verificado, haciendo petición:', {
      tokenLength: token.length,
      tokenPreview: token.substring(0, 20) + '...'
    });
    
    const response = await apiGet(API_CONFIG.ENDPOINTS.AUTH.ME);
    const data = await response.json();
    
    console.log('authService.getCurrentUser: Respuesta completa del backend:', data);
    
    if (!data.success) {
      throw new Error(data.error || 'Error al obtener información del usuario');
    }
    
    // Actualizar información del usuario en localStorage
    if (data.data) {
      const userData = {
        ...data.data,
        // Crear username completo si no existe (para compatibilidad)
        username: data.data.username || `${data.data.nombre} ${data.data.apellido || ''}`.trim() || data.data.email
      };
      
      console.log('authService.getCurrentUser: Datos del usuario procesados:', userData);
      console.log('authService.getCurrentUser: Campos específicos - telefono:', userData.telefono, 'fecha_nacimiento:', userData.fecha_nacimiento, 'region:', userData.region, 'ciudad:', userData.ciudad, 'pais:', userData.pais);
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isLoggedIn', 'true');
    }
    
    return data.data;
  } catch (error) {
    console.error('authService.getCurrentUser: Error:', error);
    throw error;
  }
};

/**
 * Obtiene los perfiles aprobados del usuario actual (endpoint ligero)
 * @returns {Promise<string[]>} - Lista de perfiles (atleta, meet_director, presidente_club, admin)
 */
export const getPerfiles = async () => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.AUTH.PERFILES);
  const data = await response.json();
  if (!data.success) return [];
  return Array.isArray(data.perfiles) ? data.perfiles : [];
};

/**
 * Cierra sesión
 */
export const logout = () => {
  clearTokens();
  localStorage.removeItem('isAdmin');
  // Disparar evento personalizado para que otros componentes se actualicen
  window.dispatchEvent(new Event('userLogout'));
};

/**
 * Verifica si el usuario está autenticado
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getAccessToken() && localStorage.getItem('isLoggedIn') === 'true';
};

/**
 * Obtiene la afiliación del usuario actual
 * @returns {Promise<object>} - Datos de afiliación
 */
export const getCurrentAffiliation = async () => {
  try {
    const response = await apiGet(API_CONFIG.ENDPOINTS.AFILIACION.ME);
    
    // Verificar si la respuesta es OK antes de parsear JSON
    if (!response.ok) {
      // Si es 404, el endpoint no existe (aún no desplegado)
      if (response.status === 404) {
        console.warn('authService.getCurrentAffiliation: Endpoint no encontrado (404). El endpoint puede no estar desplegado aún.');
        return null;
      }
      // Si es 401, problema de autenticación
      if (response.status === 401) {
        console.warn('authService.getCurrentAffiliation: No autorizado (401). Token inválido o expirado.');
        return null;
      }
      // Otro error HTTP
      const errorText = await response.text();
      console.error('authService.getCurrentAffiliation: Error HTTP', response.status, errorText);
      return null;
    }
    
    const data = await response.json();
    
    console.log('authService.getCurrentAffiliation: Respuesta del backend:', data);
    
    if (!data.ok) {
      // Si no tiene afiliación, retornar null en lugar de lanzar error
      if (data.codigo === 'SIN_AFILIACION' || data.codigo === 'SIN_AFILIACION_FEDERACION') {
        console.log('authService.getCurrentAffiliation: Usuario sin afiliación');
        return null;
      }
      console.warn('authService.getCurrentAffiliation: Respuesta no OK:', data);
      return null;
    }
    
    return data.afiliacion_actual || null;
  } catch (error) {
    console.error('authService.getCurrentAffiliation: Error:', error);
    // Si es error de conexión, puede ser que el endpoint no esté desplegado
    if (error.message && error.message.includes('conexión')) {
      console.warn('authService.getCurrentAffiliation: Error de conexión. El endpoint puede no estar desplegado aún.');
    }
    // Si hay error, retornar null en lugar de lanzar excepción
    return null;
  }
};


/**
 * Solicita recuperación de contraseña
 * @param {string} email - Email del usuario
 * @param {string} [origin] - Origen del frontend (ej: window.location.origin) para que el enlace del correo redirija al mismo sitio
 * @returns {Promise<object>} - Resultado de la solicitud
 */
export const forgotPassword = async (email, origin) => {
  try {
    const response = await apiPost(API_CONFIG.ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email,
      ...(origin && { origin })
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Error al solicitar recuperación de contraseña');
    }
    
    return data;
  } catch (error) {
    console.error('Error en forgotPassword:', error);
    throw error;
  }
};

/**
 * Resetea la contraseña usando un token
 * @param {string} token - Token de recuperación
 * @param {string} newPassword - Nueva contraseña
 * @returns {Promise<object>} - Resultado del reset
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiPost(API_CONFIG.ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      new_password: newPassword
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Error al restablecer la contraseña');
    }
    
    return data;
  } catch (error) {
    console.error('Error en resetPassword:', error);
    throw error;
  }
};

// =====================================================
// Edición del perfil propio (solo nombre, teléfono, ciudad, región y foto)
// =====================================================
const actualizarUsuarioGuardado = (cambios) => {
  try {
    const actual = JSON.parse(localStorage.getItem('user') || '{}');
    const nuevo = { ...actual, ...cambios };
    if ('nombre' in cambios) {
      nuevo.username = `${nuevo.nombre || ''} ${nuevo.apellido || ''}`.trim() || nuevo.email;
    }
    localStorage.setItem('user', JSON.stringify(nuevo));
  } catch {
    /* sin almacenamiento: se ignora */
  }
};

/**
 * Actualiza los datos editables del usuario actual.
 * @param {{nombre?: string, telefono?: string, ciudad?: string, region?: string}} cambios
 * @returns {Promise<object>} datos guardados
 */
export const updateMyProfile = async (cambios) => {
  const permitidos = ['nombre', 'telefono', 'ciudad', 'region'];
  const payload = Object.fromEntries(
    Object.entries(cambios || {}).filter(([k]) => permitidos.includes(k))
  );
  const response = await apiPut(API_CONFIG.ENDPOINTS.AUTH.ME, payload);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'No se pudo actualizar el perfil');
  actualizarUsuarioGuardado(data.data || {});
  return data.data;
};

const FOTO_TIPOS = ['image/jpeg', 'image/png', 'image/webp'];
const FOTO_MAX_MB = 5;

/** Sube la foto de perfil del usuario actual. Devuelve la nueva URL. */
export const uploadMyPhoto = async (file) => {
  if (!file) throw new Error('No se seleccionó ninguna imagen');
  if (!FOTO_TIPOS.includes(file.type)) throw new Error('Formato no permitido. Usa JPG, PNG o WEBP');
  if (file.size > FOTO_MAX_MB * 1024 * 1024) throw new Error(`La foto supera ${FOTO_MAX_MB} MB`);

  const response = await apiPostFormData(`${API_CONFIG.ENDPOINTS.AUTH.ME}/foto`, file, 'foto');
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    if (response.status === 429) throw new Error('Demasiados cambios de foto. Intenta más tarde.');
    if (response.status === 401) throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');
    throw new Error(data.error || `Error ${response.status} al subir la foto`);
  }
  const url = data.data?.foto_perfil_url || null;
  actualizarUsuarioGuardado({ foto_perfil_url: url });
  return url;
};

/** Quita la foto de perfil del usuario actual. */
export const deleteMyPhoto = async () => {
  const response = await apiDelete(`${API_CONFIG.ENDPOINTS.AUTH.ME}/foto`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'No se pudo quitar la foto');
  actualizarUsuarioGuardado({ foto_perfil_url: null });
  return null;
};
